import React, { useState } from "react";
import {
  Box,
  ListItemButton,
  ListItemText,
  Tooltip,
  IconButton,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { NavLink as RouterNavLink } from "react-router-dom";
import { OrganizationAvatar } from "@basecomponents/OrganizationAvatar";
import { AppendIfPrefixExists } from "modules/sitePaths";
import { useGetOrgUnitNavHeader } from "@features/ecommerce/hooks/useGetOrgUnitNavHeader";
import { useOrgUnitIdCurrent } from "../../../../../auth/useOrgUnitIdCurrent";
import { useClaims } from "../../../../../auth/ClaimsProvider";
import { useTenantLogo } from "shared-ui/src/shared-features/my-acccounts/hooks/useTenantLogo";
import { useGetTenantCurrentLogo } from "@features/unified-user-header-feature/hooks/useGetTenantCurrentLogo";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";
import { useFeatureFlag } from "shared-ui/src/utils/FeatureFlagHelper";
import { TenantSwitchDialog } from "./TenantSwitchDialog";
import { useGetUserTenants } from "../hooks/useUserTenants";
import { TENANT_SWITCHER_FEATURE_FLAG } from "../constants";
import { useStorefrontSwitcher } from "../storefront-switcher/StorefrontSwitcherContext";

export function OrganizationNavHeader() {
  const { getClaimValue } = useClaims();
  const [isSwitcherOpen, setIsSwitcherOpen] = useState(false);

  const orgUnitId = useOrgUnitIdCurrent();
  const accountId = getClaimValue("accountId");
  const accountName = getClaimValue("accountName") ?? "";
  const tenantName = getClaimValue("tenantName");

  const tenant = useTenantLogo({
    id: accountId?.toString(),
    useGetLogo: useGetTenantCurrentLogo,
  });

  const { data: ou, isLoading: isLoadingOrgNavHeader } = useGetOrgUnitNavHeader(
    orgUnitId,
    orgUnitId !== null,
  );

  const isLoading = tenant.isLoading || (!!orgUnitId && isLoadingOrgNavHeader);

  const { enabled: tenantSwitcherFlagEnabled = false } = useFeatureFlag(
    TENANT_SWITCHER_FEATURE_FLAG,
    false,
  );
  const storefrontSwitcher = useStorefrontSwitcher();
  const useLegacyTenantSwitcher =
    tenantSwitcherFlagEnabled && !storefrontSwitcher.enabled;

  const { data: userTenants } = useGetUserTenants(useLegacyTenantSwitcher);

  const logoUrl = tenant.logoUrl || ou?.logoUrl || undefined;
  const displayName = tenantName || ou?.displayName?.trim() || accountName;

  // Nothing to switch to with a single tenant. canSwitch is false for virtual
  // admins, who act on an account rather than a tenant. In tenant-only mode this
  // nametag is the only way back to a storefront.
  const canShowSwitcher = storefrontSwitcher.enabled
    ? storefrontSwitcher.canOpenTenant || storefrontSwitcher.canOpenStorefront
    : useLegacyTenantSwitcher &&
      (userTenants?.tenants?.length ?? 0) > 1 &&
      userTenants?.canSwitch === true;

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <ListItemButton
        component={RouterNavLink}
        to={AppendIfPrefixExists("/home")}
        sx={{
          gap: 2,
          pt: 2,
          pb: 2,
        }}
      >
        <OrganizationAvatar logoUrl={logoUrl} isLoading={isLoading} />
        <ListItemText
          primary={displayName}
          sx={{
            minWidth: 0,
            flex: "auto",
          }}
          slotProps={{
            primary: {
              variant: "subtitle1",
              fontWeight: 600,
              title: displayName,
            },
          }}
        />
        {canShowSwitcher && (
          <Tooltip title="Switch tenant">
            <IconButton
              size="small"
              aria-label="Switch tenant"
              onClick={(event) => {
                // The row itself navigates to /home; the button must not.
                event.preventDefault();
                event.stopPropagation();
                if (storefrontSwitcher.enabled) {
                  storefrontSwitcher.open("tenant_nametag");
                } else {
                  setIsSwitcherOpen(true);
                }
              }}
              {...telemetryProps({
                name: storefrontSwitcher.enabled
                  ? "storefront_switch_entry"
                  : "tenant_switch_open",
                section: storefrontSwitcher.enabled
                  ? "tenant_nametag"
                  : "side_nav",
                label: "Switch tenant",
              })}
            >
              <SwapHorizIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </ListItemButton>
      {canShowSwitcher && !storefrontSwitcher.enabled && (
        <TenantSwitchDialog
          open={isSwitcherOpen}
          onClose={() => setIsSwitcherOpen(false)}
        />
      )}
    </Box>
  );
}
